import { PageRequest } from "@shared/dtos/requests/pageRequest.dto"
import {
  BulkDeleteRegistrationRequestsRequest,
  RegistrationRequestStatusRequest,
} from "@shared/dtos/requests/registrationRequestRequests.dto"
import { HisCheckResponse } from "@shared/dtos/responses/commonResponses.dto"
import {
  BulkDeleteRegistrationRequestsResponse,
  RegistrationRequestDetailResponse,
  RegistrationRequestPageResponse,
} from "@shared/dtos/responses/registrationRequestResponses.dto"
import { Duration } from "@shared/types/duration"
import { RegistrationRequestStatusChangeResponse } from "@shared/zod/schemas/responses/registrationRequest"

import { axiosAuth } from "./generic/AxiosInterceptor/AxiosInterceptor"
import { makeFileInstance } from "./generic/FileInstance"

export const findAll = (query?: PageRequest): Promise<RegistrationRequestPageResponse> => {
  return axiosAuth({
    url: "/registrationrequests",
    method: "get",
    params: query,
  }).then((resp) => resp.data)
}

export const get = (id: string): Promise<RegistrationRequestDetailResponse> => {
  return axiosAuth({
    url: `/registrationrequests/${id}`,
    method: "get",
  }).then((resp) => resp.data)
}

export const downloadSummary = (registrationRequestId: string) => {
  return axiosAuth({
    url: `/registrationrequests/${registrationRequestId}/pdf`,
    method: "get",
    responseType: "blob",
  }).then(makeFileInstance)
}

/**
 * Downloads the unregister letter that we sent on behalf of the patient
 * NOTE: We do *not* actually send a PDF letter, but an Edifact message
 * notifying the GP. The message contains a link where they can download this
 * letter.
 */
export const downloadPreviousGpLetter = async (registrationRequestId: string) => {
  const result = await axiosAuth({
    url: `/registrationrequests/${registrationRequestId}/previousgpletter`,
    method: "get",
    responseType: "blob",
  })
  return makeFileInstance(result)
}

/**
 * Downloads the proof that the patient signed the letter
 * @deprecated - We no longer ask patients to sign the letter and thus there
 * is no proof document
 */
export const downloadPreviousGpLetterProof = async (registrationRequestId: string) => {
  const result = await axiosAuth({
    url: `/registrationrequests/${registrationRequestId}/previousgpletterproof`,
    method: "get",
    responseType: "blob",
  })
  return makeFileInstance(result)
}

export const hisCheck = (id: string): Promise<HisCheckResponse> => {
  return axiosAuth({
    url: `/registrationrequests/${id}/hischeck`,
    method: "get",
  }).then((res) => res.data)
}

export const changeStatus = (
  id: string,
  req: RegistrationRequestStatusRequest
): Promise<RegistrationRequestStatusChangeResponse> => {
  return axiosAuth({
    url: `/registrationrequests/${id}`,
    method: "patch",
    data: req,

    // Insane timeout to handle long-running acceptance operations (sending
    // patient data to ION, HIS, and there are other optional processes
    // like triggering AutoMedic and sending an email to the patient).
    //
    // TODO IT-3748: Use a job to finish the process in the
    // background. Show loading state while ongoing
    timeout: Duration.from("60 seconds").milliseconds,
  }).then((res) => res.data)
}

export const retryCov = async (id: string): Promise<void> => {
  await axiosAuth({
    url: `/registrationrequests/${id}/retry-cov`,
    method: "patch",
  })
}

/**
 * Re-sends a registration request to the HIS after a previous send failed.
 * Only valid when the request is in an error state; it re-triggers the HIS send
 * without recreating the patient or resending the acceptance email. The chosen
 * AGB code is not persisted server-side, so it must be provided here.
 * @param id - The registration request id.
 * @param agbCode - The practice AGB code to register the patient under.
 * @returns A promise that resolves once the resend has been triggered.
 */
export const resendToHis = async (id: string, agbCode: string): Promise<void> => {
  await axiosAuth({
    url: `/registrationrequests/${id}/resend-his`,
    method: "post",
    data: { agbCode },

    // Long timeout to handle the synchronous HIS / AutoMedic send, matching the
    // accept flow (see `changeStatus` above).
    timeout: Duration.from("60 seconds").milliseconds,
  })
}

export const bulkDelete = (
  req: BulkDeleteRegistrationRequestsRequest
): Promise<BulkDeleteRegistrationRequestsResponse> => {
  return axiosAuth({
    url: "/registrationrequests/bulk-delete",
    method: "post",
    data: req,
  }).then((resp) => resp.data)
}
